import { faGear, faSignOut, faUser } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { Dropdown } from '#src/atoms/dropdowns/Dropdown/index.tsx';
import { Packs } from '#src/atoms/icons/Packs.tsx';
import { StarBall } from '#src/atoms/icons/StarBall/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '#src/atoms/layout/flex/index.tsx';
import { BodyL, BodyS } from '#src/atoms/typography/index.tsx';
import { Badge } from '#src/atoms/ui/Badge/index.tsx';
import { CloseButton } from '#src/components/dialog/CloseButton/index.tsx';
import { Footer } from '#src/components/navigation/AppNavigation/NavItems/MoreNavItems/Footer/index.tsx';
import { MoreNavItems } from '#src/components/navigation/AppNavigation/NavItems/MoreNavItems/index.tsx';
import { SportSelector } from '#src/components/navigation/AppNavigation/SportSelector/index.tsx';
import { MarketCredits } from '#src/components/navigation/AppNavigation/UserCurrencies/MarketCredits/index.tsx';
import { MoneyBalances } from '#src/components/navigation/AppNavigation/UserCurrencies/MoneyBalances/index.tsx';
import {
  NavItem,
  Separator,
} from '#src/components/navigation/NavItem/index.tsx';
import {
  ANY_SPORT_MY_CLUB,
  ANY_SPORT_SERIES_PLAY,
  MY_SORARE,
  SETTINGS,
} from '#src/constants/__generated__/routes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { useWalletDrawerContext } from '#src/contexts/walletDrawer/index.tsx';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useCurrentUserCurrencies } from '#src/hooks/useCurrentUserCurrencies.tsx';
import { useInviteLink } from '#src/hooks/useInviteLink.tsx';
import { useLockProMode } from '#src/hooks/useLockProMode.ts';
import { useReferralProgramConfig } from '#src/hooks/useReferralProgramConfig.ts';
import { glossary, navLabels } from '#src/lib/glossary.ts';
import { generateSportPath } from '#src/lib/routing/generateSportPath.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';
import { regularMaterial } from '#src/style/utils.tsx';

import { GoProBlock } from './GoProBlock/index.tsx';
import { MyClubNavItem } from './MyClubNavItem/index.tsx';
import { ProfileButtonContent } from './ProfileButtonContent/index.tsx';

const DropdownContent = styled(Vertical).attrs({ gap: 2 })`
  top: 0;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  ${regularMaterial}

  @media ${laptopAndAbove} {
    width: 375px;
  }
`;

const StyledSBHorizontal = styled(SBHorizontal)`
  width: 100%;
`;

const Box = styled(Link)`
  display: flex;
  gap: var(--double-unit);
  align-items: flex-start;
  flex-direction: column;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
  flex-grow: 1;
  align-self: stretch;

  &:hover {
    background-color: rgba(var(--c-rgb-white), 0.04);
  }
`;

type Props = { sport: Sport; size?: 'small' | 'medium' };
export const UserNav = ({ sport, size }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();
  const inviteLink = useInviteLink();
  const { showDrawer } = useWalletDrawerContext();
  const { formatMessage } = useIntl();
  const isLaptopAndAbove = useIsLaptop();
  const walletContext = useWalletContext();
  const { fiatCurrency } = useCurrentUserCurrencies();
  const { userFiatAmount } = useReferralProgramConfig();
  const { formatNumber } = useIntlContext();
  const { lockProMode } = useLockProMode();
  const navigate = useNavigate();

  const amount =
    userFiatAmount &&
    formatNumber(userFiatAmount, {
      style: 'currency',
      currency: fiatCurrency,
      maximumFractionDigits: 0,
    });

  return (
    <Dropdown
      transparent
      forceDesktopBehavior={isLaptopAndAbove}
      label={({ onClick, open }) => (
        <button type="button" onClick={onClick}>
          <ProfileButtonContent sport={sport} active={open} size={size} />
        </button>
      )}
      side={isLaptopAndAbove ? 'bottom' : 'left'}
      gap={16}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          {!isLaptopAndAbove && (
            <SBHorizontal>
              <SportSelector displaySelectedSportName />
              <CloseButton onClose={closeDropdown} />
            </SBHorizontal>
          )}
          <MyClubNavItem
            to={generateSportPath(ANY_SPORT_MY_CLUB, { sport })}
            onClick={closeDropdown}
          />
          <GoProBlock onClose={closeDropdown} />
          <Horizontal>
            <Box to={MY_SORARE}>
              <StarBall width={16} height={16} />
              <Vertical gap={0.5}>
                <BodyL bold>{formatMessage(navLabels.mySorare)}</BodyL>
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="Nav.mySorare.seeWhatsnew"
                    defaultMessage="See what’s new"
                  />
                </BodyS>
              </Vertical>
            </Box>
            <Box to={inviteLink}>
              <StyledSBHorizontal>
                <FontAwesomeIcon icon={faUser} />
                <Badge
                  badgeContent={currentUser?.unclaimedReferralRewardsCount}
                  right={1}
                />
              </StyledSBHorizontal>
              <Vertical gap={0.5}>
                <BodyL bold>{formatMessage(navLabels.inviteFriends)}</BodyL>
                <BodyS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="Nav.mySorare.earnUpTo"
                    defaultMessage="Earn up to {amount}"
                    values={{ amount }}
                  />
                </BodyS>
              </Vertical>
            </Box>
          </Horizontal>
          {!isLaptopAndAbove && (
            <>
              <MoneyBalances onClick={showDrawer} />
              <button onClick={showDrawer} type="button">
                <MarketCredits sport={sport} />
              </button>
            </>
          )}
          <Vertical>
            <NavItem
              icon={() => <FontAwesomeIcon icon={faGear} />}
              label={formatMessage(navLabels.settings)}
              to={SETTINGS}
            />
            {!isLaptopAndAbove && <MoreNavItems noFooter />}

            <Separator />
            {!!currentUser?.anyUserSportProfile?.proUnlocked && (
              <NavItem
                icon={() => <Packs />}
                label={formatMessage({
                  id: 'Nav.mySorare.goSeriesOnly',
                  defaultMessage: 'Go Set Only',
                })}
                onClick={async () => {
                  await lockProMode({
                    variables: {
                      sport,
                      input: {
                        sport,
                      },
                    },
                  });
                  navigate(generateSportContextPath(ANY_SPORT_SERIES_PLAY));
                }}
              />
            )}
            <NavItem
              icon={() => <FontAwesomeIcon icon={faSignOut} />}
              label={formatMessage(glossary.logOut)}
              onClick={() => {
                walletContext.logOut();
                closeDropdown();
              }}
            />
            {!isLaptopAndAbove && (
              <>
                <Separator />
                <Footer />
              </>
            )}
          </Vertical>
        </DropdownContent>
      )}
    </Dropdown>
  );
};
